// Reporting app entry point — uses the shared startApp() pattern from @frontend/react.
//
// Three main routes matching the Angular reporting tool:
//  - /dashboard — team-level session overview (stat cards, performance chart)
//  - /player — individual player deep-dive (stats, pitch map, monitoring)
//  - /team — comparative table of all players in a session
//
// The /player/:playerId route is a direct-link variant that pre-selects a player.
//
// ReportingTeamSwitcher sits in the top nav and persists across all views,
// so team/session selection is always accessible.
import '@frontend/assets'
import type { AppConfig } from '@frontend/core'
import { startApp } from '@frontend/react'
import '@frontend/ui'
import { LayoutDashboard, User, Users } from 'lucide-react'
import { ReportingLogo } from './components/reporting-logo'
import { ReportingTopnavExtra } from './components/reporting-topnav-extra'
import { ReportingUserMenu } from './components/reporting-user-menu'
import { withReportingState } from './lib/with-reporting-state'
import { Dashboard } from './views/dashboard'
import { PlayerDetail } from './views/player-detail'
import { TeamStatsView } from './views/team-stats'

const appConfig: AppConfig = {
  name: 'Sportable',
  landingPath: '/dashboard',
  topnavExtra: ReportingTopnavExtra,
  topnavUser: ReportingUserMenu,
  topnavLogo: ReportingLogo,
  hideTopnavOrganisation: true,
  routes: [
    {
      name: 'Dashboard',
      path: '/dashboard',
      Component: withReportingState(Dashboard),
      viewLayout: 'topnav',
      mainNav: true,
      isLanding: true,
      icon: LayoutDashboard
    },
    {
      name: 'Player view',
      path: '/player',
      Component: withReportingState(PlayerDetail),
      viewLayout: 'topnav',
      mainNav: true,
      icon: User
    },
    {
      name: 'Team view',
      path: '/team',
      Component: withReportingState(TeamStatsView),
      viewLayout: 'topnav',
      mainNav: true,
      icon: Users
    },
    {
      name: 'Player',
      path: '/player/:playerId',
      Component: withReportingState(PlayerDetail),
      viewLayout: 'topnav',
      icon: User
    }
  ],
  exemptFromAuthentication: false,
  livePositionData: false,
  liveSessionData: false,
  liveEventStream: false,
  pitchSide: false
}

// Sentry: in dev, initSentry swaps this for the shared DEV_SENTRY_DSN and only
// fires when SENTRY_IN_DEV=1 is set in localStorage or env. The DSN below is
// the reporting project's prod DSN, used by production builds.
startApp(appConfig, undefined, {
  dsn: 'https://a30020547d41792f3c83357ed4798413@o241876.ingest.us.sentry.io/4511217890820096'
})
